import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import Path from 'routes/Path';
import { userRoles } from 'util/appConstants';
import { layouts } from 'components/FreecodePage/FreecodeLayoutConstants';
import {
  maxWidth,
  FreecodeNav,
  MenuIcon,
  NavbarContents,
  DropdownContent,
  NavbarContentRow,
  HeaderLink,
  LogoImg,
  LogoImgSmall,
  IconButtonContainer,
  ButtonIcon,
  UserDetailsContainer,
  UserNameContainer,
  UserName,
  UserCompanyName,
  UserProfileImage,
} from './styles';
import TourButton from './TourButton';
import ReportButton from './ReportButton';
import CreatorButtons from './CreatorButtons';
import PublishButton from './PublishButton';
import SignUp from './SignUp';
import SponsorDisplay from './SponsorDisplay';
import FreecodeProgressBar from './FreecodeProgressBar';
import { Images } from 'util/images';
import { getConfig } from 'functions/getConfig';
import { useFreecodeContext, useAppContext } from 'hooks/useContext';
import { useWindowSize } from 'hooks/useWindowSize';

export default function HeaderFreecode({ contest, headerLinks = [] }) {
  const { user, userRole, isCommunityUser, isTicketAuth, company } = useSelector((state) => state.auth);

  const { layout, test, isCreator, sponsor, ticket, submit } = useFreecodeContext();
  const { isDarkMode, toggleDarkMode } = useAppContext();
  const { width } = useWindowSize();

  const [logoRedirectUrl, setLogoRedirectUrl] = useState(Path.landingPage);
  const [userRedirectUrl, setUserRedirectUrl] = useState(Path.landingPage);
  const [actualHeaderLinks, setActualHeaderLinks] = useState(headerLinks);
  const [useSmallLogo, setUseSmallLogo] = useState(false);
  const [isDropdown, setIsDropdown] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  useEffect(() => {
    if (!isDropdown && width < maxWidth) setIsDropdown(true);
    else if (isDropdown && width > maxWidth) setIsDropdown(false);
    if (sponsor || ticket) {
      if (!useSmallLogo && width < 400) setUseSmallLogo(true);
      else if (useSmallLogo && width > 400) setUseSmallLogo(false);
    }
  }, [width, isDropdown, setIsDropdown, sponsor, ticket, useSmallLogo, setUseSmallLogo]);

  useEffect(() => {
    if (isTicketAuth) {
      // Ticket user go to ticket page
      setLogoRedirectUrl(Path.candidateTicketsPage);
      setUserRedirectUrl(Path.candidateTicketsPage);
    } else if (userRole && userRole !== userRoles.CANDIDATE) {
      // company user
      setLogoRedirectUrl(getConfig().REDIRECTION_URL);
      setUserRedirectUrl(getConfig().REDIRECTION_URL);
    } else if (isCommunityUser) {
      // Community user
      setLogoRedirectUrl(Path.landingPage);
      setUserRedirectUrl(Path.profilePage);
    } else {
      // default
      setLogoRedirectUrl(Path.landingPage);
      setUserRedirectUrl(Path.landingPage);
    }
  }, [user, userRole, isCommunityUser, isTicketAuth]);

  useEffect(() => {
    if (!isTicketAuth && userRole && userRole !== userRoles.CANDIDATE) {
      // signed in company user
      setActualHeaderLinks([
        ...headerLinks,
        { link: `${getConfig().REDIRECTION_URL}/tests`, label: 'Tests' },
        { link: Path.sendTestPage, label: 'Send Tests' },
      ]);
    } else {
      setActualHeaderLinks(headerLinks);
    }
  }, [userRole, isTicketAuth, headerLinks]);

  const getHeaderLinks = () => (
    <>
      {actualHeaderLinks.map((linkData, index) => (
        <NavbarContentRow key={index}>
          <HeaderLink href={linkData.link}>{linkData.label}</HeaderLink>
        </NavbarContentRow>
      ))}
      <div className="mr-auto" />
    </>
  );

  const getCreatorButton = () =>
    isCreator &&
    test && (
      <NavbarContentRow>
        <PublishButton />
      </NavbarContentRow>
    );

  const getRoundButtonRow = () => (
    <>
      <NavbarContentRow>
        {isCreator && test && <CreatorButtons />}
        {!isCreator && test && <ReportButton test={test} contest={contest} />}
        {layout !== layouts.small && <TourButton isDarkMode={isDarkMode} layout={layout} />}
        {/* <IconButtonContainer title="Select Layout">
            <ButtonIcon icon={'fa fa-columns'}></ButtonIcon>
          </IconButtonContainer> */}
        <IconButtonContainer title={isDarkMode ? 'Light Mode' : 'Dark Mode'} onClick={() => toggleDarkMode()}>
          <ButtonIcon icon={isDarkMode ? 'fa fa-sun-o' : 'fa fa-moon-o'}></ButtonIcon>
        </IconButtonContainer>
      </NavbarContentRow>
    </>
  );

  const getUserDetails = () => {
    return (
      <NavbarContentRow>
        <a href={userRedirectUrl}>
          <UserDetailsContainer>
            <UserNameContainer>
              <UserName>{user.name || user.coderCommunityProfile?.name}</UserName>
              {company?.name && <UserCompanyName>{company.name}</UserCompanyName>}
            </UserNameContainer>
            <UserProfileImage
              src={user.avatar?.search('/images') !== 0 ? user.avatar : Images.defaultAvatar}
              alt="profile pic"
            />
          </UserDetailsContainer>
        </a>
      </NavbarContentRow>
    );
  };

  const getDropdown = () => (
    <DropdownContent>
      {getHeaderLinks()}
      {getCreatorButton()}
      {getRoundButtonRow()}
      {userRole && getUserDetails()}
      {!userRole && <SignUp />}
    </DropdownContent>
  );

  return (
    <FreecodeNav>
      <a href={logoRedirectUrl}>
        {useSmallLogo ? <LogoImgSmall src={Images.logoOnlyWhite} alt="FS" /> : <LogoImg src={Images.logo} alt="FS" />}
      </a>
      {isDropdown && sponsor && <SponsorDisplay sponsor={sponsor} contest={contest} />}
      {isDropdown && ticket && <FreecodeProgressBar ticket={ticket} submit={submit} />}
      <MenuIcon onClick={() => setIsOpened(!isOpened)} />

      <NavbarContents>
        {getHeaderLinks()}
        {sponsor && <SponsorDisplay sponsor={sponsor} contest={contest} />}
        {ticket && <FreecodeProgressBar ticket={ticket} submit={submit} />}
        {getCreatorButton()}
        {getRoundButtonRow()}
        {userRole && getUserDetails()}
        {!userRole && <SignUp />}
      </NavbarContents>

      {isOpened && getDropdown()}
    </FreecodeNav>
  );
}
